import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AppMark } from "../components/AppMark";
import { useAuth } from "../hooks/useAuth";

/** /i/<token>: a personal invite from the owner or a manager. One tap signs
 * this person in on this device; the link then stops working. */
export function InvitePage() {
  const { token } = useParams({ strict: false }) as { token: string };
  const { acceptInvite } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pin, setPin] = useState("");
  const invite = useQuery({
    queryKey: ["invite", token],
    retry: false,
    queryFn: async (): Promise<{ name: string; workspace: string; needs_pin?: boolean }> => {
      const res = await fetch(`/api/invite/${encodeURIComponent(token)}`, { credentials: "same-origin" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "This invite can't be used.");
      return body;
    },
  });

  async function join() {
    setBusy(true);
    const result = await acceptInvite(token, pin);
    setBusy(false);
    if (!result.ok) { setError(result.error); return; }
    void navigate({ to: "/", replace: true });
  }

  return <>
    <header className="top-bar"><div className="brand"><AppMark />trim log.</div></header>
    <main className="page">
      {invite.isPending && <div className="center muted" style={{ paddingTop: 80 }}>Checking your invite…</div>}
      {invite.isError && <div className="panel" role="alert"><h2>Invite not available</h2><p>{(invite.error as Error).message}</p></div>}
      {invite.data && <div className="panel">
        <div className="hero"><h1>Hi {invite.data.name}</h1><p>You've been invited to log your trim work at <strong>{invite.data.workspace}</strong>.</p></div>
        {invite.data.needs_pin && <label className="field" style={{ display: "block", marginBottom: 12 }}>
          Choose a 4-6 digit PIN
          <input type="password" inputMode="numeric" autoComplete="new-password" pattern="[0-9]{4,6}" maxLength={6}
            data-testid="invite-pin" value={pin} onChange={(e) => setPin(e.target.value.replace(/D/g, ""))} />
          <span className="muted small">You'll use it to sign in on other phones.</span>
        </label>}
        <button type="button" className="btn btn-primary" data-testid="accept-invite" disabled={busy || (invite.data.needs_pin && !/^[0-9]{4,6}$/.test(pin))} onClick={() => void join()}>{busy ? "Joining…" : `Join as ${invite.data.name}`}</button>
        <p className="muted small" style={{ marginTop: 10 }}>This signs you in on this phone. Not {invite.data.name}? Don't tap join; tell your manager.</p>
        {error && <p role="alert" style={{ marginTop: 10 }}>{error}</p>}
      </div>}
    </main>
  </>;
}
